/**
 * Floating panels. Every dataset reachable by clicking the map is also reachable
 * here as a list or table, so the map is never the only way to do something.
 */

import type { LeaderboardResponse, MarketBookResponse, OpenOrder } from "@parcelbound/contracts";
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import {
  api,
  ApiError,
  type InventoryResponse,
  type LeaseListResponse,
  type PortfolioResponse,
} from "./api";
import { useApp, type WindowId } from "./store";
import { Window } from "./Window";

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "something went wrong";
}

/** Shared window wiring: focus tracking and close. */
function useWindowChrome(id: WindowId) {
  const focused = useApp((s) => s.focused) === id;
  const closeWindow = useApp((s) => s.closeWindow);
  const focusWindow = useApp((s) => s.focusWindow);
  return {
    focused,
    onClose: () => closeWindow(id),
    onFocus: () => focusWindow(id),
  };
}

function Money({ wire, signed = false }: { wire: string; signed?: boolean }): JSX.Element {
  const n = Number(wire);
  const tone = n > 0 ? "up" : n < 0 ? "down" : "flat";
  const body = `${signed && n > 0 ? "+" : n < 0 ? "−" : ""}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  return <span className={signed ? tone : undefined}>{body}</span>;
}

/* ------------------------------------------------------------------ */

export function PortfolioWindow(): JSX.Element {
  const chrome = useWindowChrome("portfolio");
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const select = useApp((s) => s.select);

  useEffect(() => {
    api.portfolio().then(setData, (e) => setError(describe(e)));
  }, []);

  return (
    <Window title="Portfolio" initial={{ x: 28, y: 96 }} width={520} maxHeight={520} {...chrome}>
      {error && <div className="notice err">{error}</div>}
      {!data && !error && <p className="empty">Loading…</p>}
      {data && (
        <>
          <h3>Land ({data.parcels.length})</h3>
          {data.parcels.length === 0 ? (
            <p className="empty">
              You own no land yet. Find unclaimed land on the map and buy it from the world authority.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Chunk</th>
                  <th>Use</th>
                  <th className="num">Area m²</th>
                  <th className="num">Appraisal</th>
                  <th className="num">Built</th>
                </tr>
              </thead>
              <tbody>
                {data.parcels.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => select(p.id)}>
                    <td className="mono small faint">{p.chunkId}</td>
                    <td>{p.landUse}</td>
                    <td className="num">{Math.round(p.areaM2).toLocaleString()}</td>
                    <td className="num">
                      <Money wire={p.appraisedValue} />
                    </td>
                    <td className="num">{p.buildingCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3>Buildings ({data.buildings.length})</h3>
          {data.buildings.length === 0 ? (
            <p className="empty">No structures yet. Buy vacant land and construct on it.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th className="num">Floors</th>
                  <th className="num">Units</th>
                  <th className="num">Let</th>
                  <th className="num">Appraisal</th>
                </tr>
              </thead>
              <tbody>
                {data.buildings.map((b) => (
                  <tr key={b.id} className="clickable" onClick={() => select(b.parcelId)}>
                    <td>{b.label}</td>
                    <td className="num">{b.floors}</td>
                    <td className="num">{b.unitCount}</td>
                    <td className="num">{b.leasedUnitCount}</td>
                    <td className="num">
                      <Money wire={b.appraisedValue} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Window>
  );
}

/* ------------------------------------------------------------------ */

const FAMILY_LABELS: Array<{ id: string; label: string }> = [
  { id: "all", label: "All" },
  { id: "crop", label: "Crops" },
  { id: "animal_product", label: "Animal" },
  { id: "raw_resource", label: "Raw" },
  { id: "processed_material", label: "Materials" },
  { id: "goods", label: "Goods" },
];

/** Item art for the market grid, reference-style: one friendly icon each. */
const COMMODITY_ICONS: Record<string, string> = {
  WHEAT: "🌾", CORN: "🌽", RICE: "🍚", POTATO: "🥔", TOMATO: "🍅", LETTUCE: "🥬",
  ONION: "🧅", SOYBEAN: "🫘", STRAWBERRY: "🍓", BANANA: "🍌", AVOCADO: "🥑", HAZELNUT: "🌰",
  MILK: "🥛", EGGS: "🥚", WOOL: "🐑", HIDE: "🐮", BEEF: "🥩", PORK: "🥓",
  MUTTON: "🍖", CHICKEN_MEAT: "🍗", TIMBER: "🌲", IRON_ORE: "⛏️", LIMESTONE: "🪨",
  SAND: "🏝️", CLAY: "🏺", COAL: "⚫", PLANK: "🪵", BEAM: "🏗️", NAILS: "📌",
  PAPER: "📄", IRON_BAR: "🧲", STEEL_SHEET: "🥈", WIRE: "➰", MACHINE_PARTS: "⚙️",
  CEMENT: "🪣", CONCRETE: "🗿", BRICK: "🧱", GLASS: "🪟", YARN: "🧶", FABRIC: "🧵",
  LEATHER: "👝", FLOUR: "🥡", SUGAR: "🍬", OIL: "🫒", DOUGH: "🥟", BREAD: "🍞",
  BUNS: "🥖", BURGER_BUN: "🥯", TORTILLA: "🫓", BUTTER: "🧈", CHEESE: "🧀",
  PATTY: "🍔", SAUSAGE: "🌭", KETCHUP: "🥫", MUSTARD: "🧴", RELISH: "🥒",
  SALSA: "🌶️", GUACAMOLE: "🥣", SANDWICH: "🥪", PIZZA: "🍕", BURRITO: "🌯",
  SUSHI_ROLL: "🍣", SALAD: "🥗", SOUP: "🍲", PIE: "🥧", JUICE: "🧃",
  CHOCOLATE: "🍫", SHIRT: "👕",
};
const FAMILY_FALLBACK_ICONS: Record<string, string> = {
  crop: "🌱",
  animal_product: "🐄",
  raw_resource: "🪨",
  processed_material: "📦",
  goods: "🛍️",
};

export function ExchangeWindow(): JSX.Element {
  const chrome = useWindowChrome("exchange");
  const notify = useApp((s) => s.notify);
  const [tab, setTab] = useState<"market" | "orders">("market");
  const [commodities, setCommodities] = useState<
    Array<{ id: string; name: string; family: string; basePrice: string }>
  >([]);
  const [family, setFamily] = useState("all");
  const [search, setSearch] = useState("");
  /** Market tab is a two-level view: the item grid, then one item's page. */
  const [view, setView] = useState<"grid" | "detail">("grid");
  const [selected, setSelected] = useState("PLANK");
  const [book, setBook] = useState<MarketBookResponse | null>(null);
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [orders, setOrders] = useState<OpenOrder[]>([]);
  const [side, setSide] = useState<"buy" | "sell">("buy");
  const [price, setPrice] = useState("1.00");
  const [qty, setQty] = useState(10);
  const [busy, setBusy] = useState(false);

  const reload = async (commodity: string): Promise<void> => {
    try {
      const [b, inv, o] = await Promise.all([api.book(commodity), api.inventory(), api.orders()]);
      setBook(b);
      setInventory(inv);
      setOrders(o.orders);
      setPrice(b.lastPrice ?? b.basePrice);
    } catch (err) {
      notify("err", describe(err));
    }
  };

  useEffect(() => {
    api.commodities().then(
      (r) => setCommodities(r.commodities),
      (e) => notify("err", describe(e)),
    );
  }, []);

  useEffect(() => {
    void reload(selected);
  }, [selected]);

  const filtered = useMemo(() => {
    const byFamily = family === "all" ? commodities : commodities.filter((c) => c.family === family);
    const q = search.trim().toLowerCase();
    return q === "" ? byFamily : byFamily.filter((c) => c.name.toLowerCase().includes(q));
  }, [commodities, family, search]);

  const submit = async (marketOrder: boolean): Promise<void> => {
    setBusy(true);
    try {
      const r = await api.placeOrder(selected, side, marketOrder ? null : price, qty);
      notify(
        "ok",
        r.filledQty > 0
          ? `Filled ${r.filledQty} at an average of $${average(r.fills)}. Wallet $${r.wallet}.`
          : `Order resting for ${r.restingQty}.`,
      );
      await reload(selected);
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await api.cancelOrder(id);
      notify("ok", "Order cancelled and its reserve released.");
      await reload(selected);
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const tabs = (
    <div className="tabs" role="tablist">
      <button role="tab" aria-selected={tab === "market"} onClick={() => setTab("market")}>
        Market
      </button>
      <button role="tab" aria-selected={tab === "orders"} onClick={() => setTab("orders")}>
        My orders <span className="count">{orders.length}</span>
      </button>
    </div>
  );

  return (
    <Window
      title="Parcelbound Exchange"
      initial={{ x: 300, y: 132 }}
      width={560}
      maxHeight={600}
      tabs={tabs}
      {...chrome}
    >
      {tab === "orders" ? (
        orders.length === 0 ? (
          <p className="empty">No resting orders. Place a limit order from the Market tab.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Commodity</th>
                <th>Side</th>
                <th className="num">Price</th>
                <th className="num">Filled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}>
                  <td>{o.commodity.replace(/_/g, " ")}</td>
                  <td className={o.side === "buy" ? "up" : "down"}>{o.side}</td>
                  <td className="num">
                    <Money wire={o.price} />
                  </td>
                  <td className="num">
                    {o.filled}/{o.qty}
                  </td>
                  <td className="num">
                    <button disabled={busy} onClick={() => void cancel(o.id)}>
                      Cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : view === "grid" ? (
        <>
          <input
            type="search"
            className="xch-search"
            placeholder="Search the market"
            aria-label="Search the market"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="chips">
            {FAMILY_LABELS.map((f) => (
              <button key={f.id} aria-pressed={family === f.id} onClick={() => setFamily(f.id)}>
                {f.label}
              </button>
            ))}
          </div>

          <div className="commodity-grid" role="listbox" aria-label="Commodity">
            {filtered.map((c) => (
              <button
                key={c.id}
                role="option"
                aria-selected={selected === c.id}
                className="commodity"
                title={c.name}
                onClick={() => {
                  setSelected(c.id);
                  setView("detail");
                }}
              >
                <span className="art" aria-hidden>
                  {COMMODITY_ICONS[c.id] ?? FAMILY_FALLBACK_ICONS[c.family] ?? "❔"}
                </span>
                <span className="nm">{c.name}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <button className="xch-back" onClick={() => setView("grid")}>
            ← All items
          </button>

          <ItemHead
            commodity={commodities.find((c) => c.id === selected) ?? null}
            held={book?.youHold ?? 0}
          />

          <SpreadCards book={book} />

          {book && (
            <div className="card">
              <div className="kv">
                <span className="k">Reference price</span>
                <span className="v">
                  <Money wire={book.basePrice} />
                </span>
              </div>
              <div className="kv">
                <span className="k">Last traded</span>
                <span className="v">{book.lastPrice ? <Money wire={book.lastPrice} /> : "—"}</span>
              </div>
              <div className="kv">
                <span className="k">24h volume</span>
                <span className="v">{book.volume24h.toLocaleString()}</span>
              </div>
            </div>
          )}

          <div className="book">
            <div>
              <h3>Bids</h3>
              <table>
                <tbody>
                  {(book?.bids ?? []).map((l, i) => (
                    <tr key={i}>
                      <td className="num bid">
                        <Money wire={l.price} />
                      </td>
                      <td className="num">{l.qty}</td>
                    </tr>
                  ))}
                  {(book?.bids ?? []).length === 0 && (
                    <tr>
                      <td className="empty">No bids</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div>
              <h3>Asks</h3>
              <table>
                <tbody>
                  {(book?.asks ?? []).map((l, i) => (
                    <tr key={i}>
                      <td className="num ask">
                        <Money wire={l.price} />
                      </td>
                      <td className="num">{l.qty}</td>
                    </tr>
                  ))}
                  {(book?.asks ?? []).length === 0 && (
                    <tr>
                      <td className="empty">No asks</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="side-toggle" role="tablist" aria-label="Order side">
            <button
              role="tab"
              className="buy"
              aria-selected={side === "buy"}
              onClick={() => setSide("buy")}
            >
              Buy
            </button>
            <button
              role="tab"
              className="sell"
              aria-selected={side === "sell"}
              onClick={() => setSide("sell")}
            >
              Sell
            </button>
          </div>

          <InstantPanel
            side={side}
            book={book}
            qty={qty}
            setQty={setQty}
            busy={busy}
            onSubmit={() => void submit(true)}
          />

          <h3>Resting order</h3>
          <div className="card">
            <div className="chips">
              {quickPrices(book, side).map(([label, value]) => (
                <button key={label} onClick={() => setPrice(value)}>
                  {label}
                </button>
              ))}
            </div>
            <div className="field-row">
              <div className="field">
                <label htmlFor="price">Limit price</label>
                <input id="price" value={price} onChange={(e) => setPrice(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="qty">Quantity</label>
                <input
                  id="qty"
                  type="number"
                  min={1}
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
            </div>
            <button style={{ width: "100%" }} disabled={busy} onClick={() => void submit(false)}>
              Place {side} order
            </button>
            <p className="notice info" style={{ margin: "10px 0 0" }}>
              Buy orders reserve cash and sell orders reserve goods the moment they are placed, so a
              resting order is always covered and a fill can never bounce.
            </p>
          </div>
        </>
      )}

      {tab === "market" && view === "grid" && (
        <>
          <h3>Holdings</h3>
          {inventory && inventory.items.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Family</th>
                  <th className="num">Qty</th>
                </tr>
              </thead>
              <tbody>
                {inventory.items.map((i) => (
                  <tr key={i.commodity}>
                    <td>{i.name}</td>
                    <td className="small faint">{i.family.replace(/_/g, " ")}</td>
                    <td className="num">{i.qty.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="empty">
              You hold no goods yet. Buy some here — the world quotes every starter commodity — or
              produce your own from the Businesses window.
            </p>
          )}
        </>
      )}
    </Window>
  );
}

/* ----- exchange detail pieces ----- */

function ItemHead({
  commodity,
  held,
}: {
  commodity: { id: string; name: string; family: string } | null;
  held: number;
}): JSX.Element | null {
  if (!commodity) return null;
  return (
    <div className="xch-head">
      <span className="art" aria-hidden>
        {COMMODITY_ICONS[commodity.id] ?? FAMILY_FALLBACK_ICONS[commodity.family] ?? "❔"}
      </span>
      <span>
        <span className="nm">{commodity.name}</span>
        <span className="sub">
          {commodity.family.replace(/_/g, " ")} · {held > 0 ? `${held.toLocaleString()} held` : "none held"}
        </span>
      </span>
    </div>
  );
}

/** Best ask / spread / best bid, the reference's three stat cards. */
function SpreadCards({ book }: { book: MarketBookResponse | null }): JSX.Element | null {
  if (!book) return null;
  const ask = book.asks[0] ? Number(book.asks[0].price) : null;
  const bid = book.bids[0] ? Number(book.bids[0].price) : null;
  const spread =
    ask !== null && bid !== null && ask + bid > 0 ? ((ask - bid) / ((ask + bid) / 2)) * 100 : null;
  return (
    <div className="xch-spread">
      <div className="cell buy">
        <span className="k">Buy / ea</span>
        <span className="v">{ask !== null ? <Money wire={ask.toFixed(2)} /> : "—"}</span>
      </div>
      <div className="cell mid">
        <span className="k">Spread</span>
        <span className="v">{spread !== null ? `${spread.toFixed(1)}%` : "—"}</span>
      </div>
      <div className="cell sell">
        <span className="k">Sell / ea</span>
        <span className="v">{bid !== null ? <Money wire={bid.toFixed(2)} /> : "—"}</span>
      </div>
    </div>
  );
}

function InstantPanel({
  side,
  book,
  qty,
  setQty,
  busy,
  onSubmit,
}: {
  side: "buy" | "sell";
  book: MarketBookResponse | null;
  qty: number;
  setQty: (n: number) => void;
  busy: boolean;
  onSubmit: () => void;
}): JSX.Element {
  const wallet = Number(useApp((s) => s.dashboard?.wallet) ?? 0);
  const ea = side === "buy" ? (book?.asks[0]?.price ?? null) : (book?.bids[0]?.price ?? null);
  const eaN = ea === null ? null : Number(ea);
  // "Max": what the wallet can cover, or everything held.
  const max =
    side === "buy"
      ? eaN && eaN > 0
        ? Math.max(1, Math.floor(wallet / eaN))
        : 999
      : Math.max(1, book?.youHold ?? 1);
  const total = eaN === null ? null : eaN * qty;

  return (
    <div className={`card instant ${side}`}>
      <div className="kv">
        <span className="k" style={{ fontWeight: 800, color: "var(--ink)" }}>
          Instant {side}
        </span>
        <span className="v">{eaN !== null ? <Money wire={eaN.toFixed(2)} /> : "no offers"} / ea</span>
      </div>
      <div className="qty-row">
        <button aria-label="Halve quantity" onClick={() => setQty(Math.max(1, Math.floor(qty / 2)))}>
          −
        </button>
        <input
          aria-label="Quantity"
          value={qty}
          onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
        />
        <button aria-label="Double quantity" onClick={() => setQty(Math.min(9_999, qty * 2))}>
          +
        </button>
        <span className="presets">
          {[16, 64, 256].map((n) => (
            <button key={n} onClick={() => setQty(n)}>
              {n}
            </button>
          ))}
          <button onClick={() => setQty(Math.min(9_999, max))}>Max</button>
        </span>
      </div>
      <div className="kv total">
        <span className="k">{side === "buy" ? "Total cost" : "Total proceeds"}</span>
        <span className="v">{total !== null ? <Money wire={total.toFixed(2)} /> : "—"}</span>
      </div>
      <button
        className="primary"
        style={{ width: "100%" }}
        disabled={busy || eaN === null}
        onClick={onSubmit}
      >
        {side === "buy" ? `Buy ${qty} now` : `Sell ${qty} now`}
      </button>
    </div>
  );
}

/** Quick limit prices: join the best, beat it by a cent, or sit at the mid. */
function quickPrices(book: MarketBookResponse | null, side: "buy" | "sell"): Array<[string, string]> {
  if (!book) return [];
  const bid = book.bids[0] ? Number(book.bids[0].price) : null;
  const ask = book.asks[0] ? Number(book.asks[0].price) : null;
  const out: Array<[string, string]> = [];
  const best = side === "buy" ? bid : ask;
  if (best !== null) {
    out.push([`Best ${side === "buy" ? "bid" : "ask"}`, best.toFixed(2)]);
    const beat = side === "buy" ? best + 0.01 : Math.max(0.01, best - 0.01);
    out.push([`Best ${side === "buy" ? "+" : "−"}$0.01`, beat.toFixed(2)]);
  }
  if (bid !== null && ask !== null) {
    out.push([`Mid $${((bid + ask) / 2).toFixed(2)}`, ((bid + ask) / 2).toFixed(2)]);
  } else {
    out.push([`Reference $${Number(book.basePrice).toFixed(2)}`, Number(book.basePrice).toFixed(2)]);
  }
  return out;
}

function average(fills: Array<{ qty: number; price: string }>): string {
  const q = fills.reduce((a, f) => a + f.qty, 0);
  if (q === 0) return "—";
  const total = fills.reduce((a, f) => a + Number(f.price) * f.qty, 0);
  return (total / q).toFixed(2);
}

/* ------------------------------------------------------------------ */

export function BankWindow(): JSX.Element {
  const chrome = useWindowChrome("bank");
  const dashboard = useApp((s) => s.dashboard);
  const notify = useApp((s) => s.notify);
  const [tab, setTab] = useState<"accounts" | "income" | "transfers">("accounts");
  const [leases, setLeases] = useState<LeaseListResponse | null>(null);
  const [ledger, setLedger] = useState<Array<{ reason: string; amount: string }>>([]);
  const [amount, setAmount] = useState("1000.00");
  const [direction, setDirection] = useState<"to_savings" | "to_wallet">("to_savings");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.leases().then(setLeases, () => undefined);
    api.ledger().then((r) => setLedger(r.entries), () => undefined);
  }, []);

  const rentIn = (leases?.leases ?? [])
    .filter((l) => l.role === "landlord")
    .reduce((a, l) => a + Number(l.rentPerDay), 0);
  const rentOut = (leases?.leases ?? [])
    .filter((l) => l.role === "tenant")
    .reduce((a, l) => a + Number(l.rentPerDay), 0);

  const byReason = useMemo(() => {
    const totals = new Map<string, number>();
    for (const e of ledger) {
      totals.set(e.reason, (totals.get(e.reason) ?? 0) + Number(e.amount));
    }
    return [...totals.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  }, [ledger]);

  const transfer = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.savingsTransfer(direction, amount);
      notify("ok", direction === "to_savings" ? "Moved into savings." : "Moved into checking.");
      useApp.getState().setDashboard(await api.dashboard());
    } catch (err) {
      notify("err", describe(err));
    } finally {
      setBusy(false);
    }
  };

  const tabs = (
    <div className="tabs" role="tablist">
      <button role="tab" aria-selected={tab === "accounts"} onClick={() => setTab("accounts")}>
        Accounts
      </button>
      <button role="tab" aria-selected={tab === "income"} onClick={() => setTab("income")}>
        Income
      </button>
      <button role="tab" aria-selected={tab === "transfers"} onClick={() => setTab("transfers")}>
        Transfers
      </button>
    </div>
  );

  return (
    <Window
      title="Parcelbound Bank"
      initial={{ x: 74, y: 168 }}
      width={470}
      maxHeight={540}
      tabs={tabs}
      {...chrome}
    >
      {tab === "accounts" && (
        <>
          <div className="account">
            <span className="glyph checking">C</span>
            <span className="meta">
              <span className="name">Checking</span>
              <br />
              <span className="sub">Operating</span>
            </span>
            <span className="amount">
              <span className="big">
                <Money wire={dashboard?.wallet ?? "0.00"} />
              </span>
            </span>
          </div>
          <div className="account">
            <span className="glyph savings">S</span>
            <span className="meta">
              <span className="name">Savings</span>
              <br />
              <span className="sub">Earns hourly</span>
            </span>
            <span className="amount">
              <span className="big">
                <Money wire={dashboard?.savings ?? "0.00"} />
              </span>
              <br />
              <span className="rate">1.50% / game month</span>
            </span>
          </div>
          <p className="notice info">
            Savings earn 1.50% per 30 game days, credited every game day by the world authority.
          </p>
        </>
      )}

      {tab === "income" && (
        <>
          <h3>Passive P&amp;L (per game day)</h3>
          <div className="card">
            <div className="kv">
              <span className="k">Rent income</span>
              <span className="v">
                <Money wire={rentIn.toFixed(2)} signed />
              </span>
            </div>
            <div className="kv">
              <span className="k">Rent paid</span>
              <span className="v">
                <Money wire={(-rentOut).toFixed(2)} signed />
              </span>
            </div>
            <div className="kv">
              <span className="k">Savings interest</span>
              <span className="v">
                <Money
                  wire={(Number(dashboard?.savings ?? "0") * 0.015 / 30).toFixed(2)}
                  signed
                />
              </span>
            </div>
            <div className="kv total">
              <span className="k">Passive P&amp;L</span>
              <span className="v">
                <Money wire={dashboard?.incomePerGameDay ?? "0.00"} signed />
              </span>
            </div>
          </div>
          <p className="notice info">
            Only income that keeps flowing while you are away counts here. Rent charges and pays out
            on its own; buying and selling on the exchange moves money while you play, so it is not
            in this rate.
          </p>

          <h3>Recent activity (by reason)</h3>
          {byReason.length === 0 ? (
            <p className="empty">No ledger activity yet.</p>
          ) : (
            <div className="card">
              {byReason.map(([reason, total]) => (
                <div className="kv" key={reason}>
                  <span className="k">{reason.replace(/_/g, " ")}</span>
                  <span className="v">
                    <Money wire={total.toFixed(2)} signed />
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === "transfers" && (
        <>
          <div className="field">
            <label htmlFor="dir">Direction</label>
            <select
              id="dir"
              value={direction}
              onChange={(e) => setDirection(e.target.value as typeof direction)}
            >
              <option value="to_savings">Checking → Savings</option>
              <option value="to_wallet">Savings → Checking</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="amt">Amount</label>
            <input id="amt" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <button className="primary" disabled={busy} onClick={() => void transfer()}>
            Transfer
          </button>
        </>
      )}
    </Window>
  );
}

/* ------------------------------------------------------------------ */

export function LeasesWindow(): JSX.Element {
  const chrome = useWindowChrome("leases");
  const [data, setData] = useState<LeaseListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.leases().then(setData, (e) => setError(describe(e)));
  }, []);

  return (
    <Window title="Leases" initial={{ x: 120, y: 210 }} width={560} maxHeight={440} {...chrome}>
      {error && <div className="notice err">{error}</div>}
      {!data && !error && <p className="empty">Loading…</p>}
      {data &&
        (data.leases.length === 0 ? (
          <p className="empty">
            No active leases. List a unit you own for rent from the inspector, then seek an NPC
            tenant.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Unit</th>
                <th>Building</th>
                <th>Counterparty</th>
                <th className="num">Rent / day</th>
              </tr>
            </thead>
            <tbody>
              {data.leases.map((l) => (
                <tr key={l.id}>
                  <td>
                    <span className={`pill-tag${l.role === "landlord" ? " mine" : ""}`}>{l.role}</span>
                  </td>
                  <td>{l.unitLabel}</td>
                  <td className="faint">{l.buildingLabel}</td>
                  <td>{l.counterpartyName}</td>
                  <td className="num">
                    <Money wire={l.rentPerDay} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </Window>
  );
}

/* ------------------------------------------------------------------ */

export function LeaderboardWindow(): JSX.Element {
  const chrome = useWindowChrome("leaderboard");
  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const session = useApp((s) => s.session);

  useEffect(() => {
    api.leaderboard().then(setData, (e) => setError(describe(e)));
  }, []);

  return (
    <Window
      title="Leaderboards"
      initial={{ x: 700, y: 96 }}
      width={420}
      maxHeight={520}
      {...chrome}
    >
      {error && <div className="notice err">{error}</div>}
      {!data && !error && <p className="empty">Loading…</p>}
      {data && (
        <>
          <h3>Net worth · {data.total} player(s)</h3>
          <table>
            <thead>
              <tr>
                <th className="num">#</th>
                <th>Player</th>
                <th className="num">Net worth</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.playerId} className={e.playerId === session?.id ? "me" : undefined}>
                  <td className="num">{e.rank}</td>
                  <td>{e.displayName}</td>
                  <td className="num">
                    <Money wire={e.value} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Window>
  );
}
